import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Calendar, Send, Clock, MessageSquare, Linkedin, Twitter, Check, AlertCircle } from 'lucide-react';
import '../styles/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('http://localhost:8000/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: error.message || 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail />,
      title: "Email",
      value: "contact@kosmozconsulting.com",
      link: "mailto:contact@kosmozconsulting.com"
    },
    {
      icon: <MapPin />,
      title: "Location",
      value: "California, USA ",
      link: null
    },
  ];

  const socialLinks = [
    { icon: <Linkedin />, name: "LinkedIn", url: "#" },
  ];

  return (
    <div className="contact-page">

      {/* HERO */}
      <section className="contact-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="contact-hero-content"
          >
            <h1 className="contact-hero-title">Let's Connect</h1>
            <p className="contact-hero-subtitle">
              We partner with organizations navigating AI adoption, GTM strategy, or product acceleration.
              Whether your need is well-defined or still at the idea stage, our team is prepared to support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="section contact-main">
        <div className="container">
          <div className="contact-grid">

            {/* FORM */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="contact-form-section"
            >
              <h2 className="form-title">Book a Strategy Consultation</h2>
              <p className="form-subtitle">Fill out the form below and we'll get back to you within 24 hours</p>

              {submitStatus.message && (
                <div className={`alert alert-${submitStatus.type}`}>
                  {submitStatus.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
                  {submitStatus.message}
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit}>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="contact@kosmozconsulting.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="ai-consulting">AI Consulting</option>
                    <option value="gtm-strategy">GTM Strategy</option>
                    <option value="product-development">Product Development</option>
                    <option value="general">General Inquiry</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your project or challenge..."
                  />
                </div>

                <button type="submit" className="btn btn-primary btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <Send size={18} />}
                </button>

              </form>
            </motion.div>

            {/* CONTACT INFO */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="contact-info-section"
            >
              <div className="info-card">
                <h3 className="info-title">Get in Touch</h3>
                <p className="info-description">
                  Ready to accelerate your AI and GTM strategy? Let's discuss how we can help.
                </p>

                <div className="info-items">
                  {contactInfo.map((item, index) => (
                    <div key={index} className="info-item">
                      <div className="info-icon">{item.icon}</div>
                      <div className="info-content">
                        <h4>{item.title}</h4>
                        {item.link ? (
                          <a href={item.link}>{item.value}</a>
                        ) : (
                          <p>{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>



              <div className="info-card">
                <h3 className="info-title">Follow Us</h3>
                <div className="social-links-contact">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      className="social-link-contact"
                      aria-label={social.name}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

          </div>

          {/* CTA Bottom */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="contact-cta"
          >
            <h2>Your next step toward clarity starts here</h2>
            <p>We're excited to learn about your challenges and explore how we can help</p>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

function ArrowRight({ size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  );
}

export default Contact;